import { useRef, useState } from 'react'
import { ImagePlus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useCreateServiceRequest, useUploadRequestAttachment } from '@/hooks/use-requests'
import { getApiErrorMessage } from '@/lib/api'
import type { ServiceRequestType } from '@riverpark/types'

const SELECT_CLS =
  'flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20'

const TYPES: { value: ServiceRequestType; label: string }[] = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'billing_query', label: 'Billing query' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'general', label: 'General' },
]

export function NewRequestModal({ onClose }: { onClose: () => void }) {
  const create = useCreateServiceRequest()
  const uploadAttachment = useUploadRequestAttachment()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [type, setType] = useState<ServiceRequestType>('general')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [photos, setPhotos] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)
  const [saveStep, setSaveStep] = useState<string | null>(null)

  function handlePhotosChange(e: React.ChangeEvent<HTMLInputElement>) {
    setPhotos((prev) => [...prev, ...Array.from(e.target.files ?? [])])
    e.target.value = ''
  }

  function removePhoto(index: number) {
    setPhotos((prev) => prev.filter((_, i) => i !== index))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      setSaveStep('Submitting request…')
      const { data: created } = await create.mutateAsync({ type, subject, body })
      for (const [index, photo] of photos.entries()) {
        setSaveStep(`Uploading photo ${index + 1} of ${photos.length}…`)
        await uploadAttachment.mutateAsync({ requestId: created.id, file: photo })
      }
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to submit request'))
    } finally {
      setSaveStep(null)
    }
  }

  const pending = create.isPending || uploadAttachment.isPending

  return (
    <Modal open onClose={onClose} labelledBy="new-request-title">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <h2 id="new-request-title" className="font-serif text-xl text-ink">
            New service request
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody className="space-y-4">
          <div className="space-y-1.5">
            <label className="font-sans text-sm font-medium text-ink">Type</label>
            <select
              className={SELECT_CLS}
              value={type}
              onChange={(e) => setType(e.target.value as ServiceRequestType)}
            >
              {TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            minLength={5}
            maxLength={200}
          />
          <div className="space-y-1.5">
            <label htmlFor="request-body" className="font-sans text-sm font-medium text-ink">
              Details
            </label>
            <textarea
              id="request-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              required
              minLength={20}
              className="flex w-full rounded border border-input bg-card px-3.5 py-2 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
            />
          </div>

          <div className="space-y-1.5">
            <label className="font-sans text-sm font-medium text-ink">Photos (optional)</label>
            {photos.length > 0 && (
              <ul className="space-y-1">
                {photos.map((photo, index) => (
                  <li
                    key={`${photo.name}-${index}`}
                    className="flex items-center justify-between gap-2 rounded border border-border-2 bg-cream-2 px-2.5 py-1.5 font-sans text-xs text-ink"
                  >
                    <span className="truncate">{photo.name}</span>
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      aria-label={`Remove ${photo.name}`}
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/heic,image/heif"
              multiple
              className="hidden"
              onChange={handlePhotosChange}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 rounded border border-dashed border-border-2 px-3 py-2 font-sans text-xs font-medium text-muted-foreground transition-colors hover:border-primary hover:text-ink"
            >
              <ImagePlus size={14} />
              Add photo
            </button>
          </div>

          {error && (
            <p className="font-sans text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={pending}>
            {saveStep ?? 'Submit request'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
